import axios from "axios";
import { store } from "@/store";
import AuthService from "@/services/auth.service";
import router from "@/router";
import ToastService from "@/services/toast.service";
import * as Sentry from "@sentry/capacitor";

/**
 * What withOrderRefresh() managed to do.
 *
 * The two outcomes are separate on purpose: a photo that failed to upload and a
 * price that failed to save need different words, and reporting one as the other
 * sends the mechanic looking in the wrong place.
 */
export interface OrderRefreshResult {
	/** The wrapped request itself went through. */
	actionSucceeded: boolean;
	/** No order line edit is left waiting to be saved. */
	orderLinesArePersisted: boolean;
	/** Whatever the action threw, for the caller to report. */
	error?: unknown;
}

export default {
	/**
	 * Retrieve the azure login url from the API.
	 */
	getAllOrders: async function () {
		await axios
			.get(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments?finished=${store.getters["planning/getSegmentState"]}`,
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
			.then((res) => {
				const planning = res.data.data;

				// If we retrieved a planning, set the planning in store.
				if (planning) {
					store.commit("planning/setPlanning", planning);
				}
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		});
	},

	/**
	 * Checks if a product serial number is correct.
	 * @param product
	 * @param serialNumber
	 */
	checkProductSerialNumber: async function (
		product: any,
		serialNumber: string
	) {
		let returnVal = null;
		await axios
			.post(
				process.env.VUE_APP_API_URL +
					"/mechanic/products/" +
					product.id +
					"/check-serial-number",
				{
					serialNumber: serialNumber,
				},
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
		)
		.then()
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);

				if (error.response.data.errors.serialNumber[0]) {
					returnVal = error.response.data.errors.serialNumber[0];
				}
			}
		})
			.finally(async () => {
				await store.dispatch(
					"general/setIsUpdating",
					{ isUpdating: false },
					{ root: true }
				);
			});

		return returnVal;
	},

	/**
	 * Updates the e-mail address of a customer.
	 *
	 * Wrapped in withOrderRefresh() because the refresh afterwards would
	 * otherwise discard a price or quantity the mechanic has not saved yet.
	 * @param id
	 * @param email
	 */
	updateCustomerEmailAddress: async function (
		id: number,
		email: string
	): Promise<OrderRefreshResult> {
		await store.dispatch(
			"general/setIsUpdating",
			{ isUpdating: true },
			{ root: true }
		);

		try {
			const result = await this.withOrderRefresh(() =>
				axios.post(
					process.env.VUE_APP_API_URL +
						"/mechanic/appointments/" +
						id +
						"/email",
					{ email: email },
					{
						headers: store.getters["auth/getAuthHeaders"],
					}
				)
			);

			if (!result.actionSucceeded) {
				const error = result.error as any;

				Sentry.captureException(error);
				if (error?.response) {
					AuthService.checkForErrors(error);
				}
			}

			return result;
		} finally {
			await store.dispatch(
				"general/setIsUpdating",
				{ isUpdating: false },
				{ root: true }
			);
		}
	},

	/**
	 * Retrieve an order by given id.
	 * @param id
	 */
	getOrderById: async function (id: number) {
		await axios
			.get(process.env.VUE_APP_API_URL + "/mechanic/appointments/" + id, {
				headers: store.getters["auth/getAuthHeaders"],
			})
		.then((res) => {
			// Dispatch an action to set the current selected order to be the fetched one.
			const order = res.data.data;

			order.images = order.images.map((image: any) => ({
				...image,
				uploadedAt: image.uploaded_at,
			}));

			store.dispatch("order/setSelectedOrder", order);
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		});
	},

	/**
	 * Fetch all addable products.
	 */
	fetchAddableProducts: async function () {
		await axios
			.get(process.env.VUE_APP_API_URL + "/products", {
				headers: store.getters["auth/getAuthHeaders"],
			})
		.then((res) => {
			store.dispatch("order/setAddableProducts", res.data.data);
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		});
	},

	/**
	 * Fetch all addable product groups.
	 */
	fetchAddableProductGroups: async function () {
		await axios
			.get(process.env.VUE_APP_API_URL + "/mechanic/product-groups", {
				headers: store.getters["auth/getAuthHeaders"],
			})
			.then((res) => {
				const productGroups = res.data.data;

				const productsWithoutGroups = productGroups.reduce(
					(acc: any, group: any) => {
						const groupProducts = group.products.map(
							(product: any) => ({
								...product,
								groupDescription: group.description,
								groupId: group.id,
							})
						);

						acc.push(...groupProducts);

						return acc;
					},
					[]
				);

			store.dispatch(
				"order/setAddableProducts",
				productsWithoutGroups
			);
			store.dispatch("order/setAddableProductGroups", res.data.data);
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		});
	},

	/**
	 * Update the selected order.
	 */
	updateOrder: async function () {
		return await axios
			.post(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${store.getters["order/getSelectedOrder"].id}`,
				store.getters["order/getSelectedOrder"],
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
			.then((res) => {
				const order = res.data.data;

				order.images = order.images.map((image: any) => ({
					...image,
					uploadedAt: image.uploaded_at,
				}));

		store.dispatch("order/setSelectedOrder", order);

		// Order is updated.
		return true;
	})
	.catch((error) => {
		Sentry.captureException(error);
		if (error.response) {
			AuthService.checkForErrors(error);
		}

		return false;
	});
	},

	/**
	 * Formats to an array of error messages
	 * @param errorArray
	 */
	formatSerialNumberErrors(errorArray: object) {
		const arrayOfMessages = [] as any;

		if (errorArray) {
			// Loop through the error object.
			Object.values(errorArray).forEach((errorArray: any) => {
				// Loop through the errorArray
				errorArray.forEach((error: any) => {
					// If the array does not exist in the array messages yet
					if (
						!arrayOfMessages.find(
							(errorMessage: any) => errorMessage === error
						)
					) {
						arrayOfMessages.push(error);
					}
				});
			});
		}

		// Return the array of messages.
		return arrayOfMessages;
	},

	/**
	 * Add serial numbers to the product in Exact Online
	 */
	addSerialNumbers: async function () {
		await store.dispatch(
			"general/setIsUpdating",
			{ isUpdating: true },
			{ root: true }
		);
		await axios
			.post(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${store.getters["order/getSelectedOrder"].id}/serial-numbers`,
				store.getters["order/getSelectedOrder"].order_lines,
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
		.then(() => {
			// Order is updated.
			router.push({
				name: "OrderAgreement",
				params: { id: store.getters["order/getSelectedOrder"].id },
			});
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		})
			.finally(() => {
				store.dispatch(
					"general/setIsUpdating",
					{ isUpdating: false },
					{ root: true }
				);
			});
	},

	/**
	 * Sends the payment method to the API.
	 * @param method
	 */
	sendPaymentMethod: async function (method: string) {
		try {
			await axios
				.post(
					process.env.VUE_APP_API_URL +
						`/mechanic/appointments/${store.getters["order/getSelectedOrder"].id}/checkout`,
					{ payment_method: method },
					{
						headers: store.getters["auth/getAuthHeaders"],
					}
				)
				.then((res) => {
					if (res.data.qr_code) {
						store.dispatch(
							"order/setOnlineBankingQRCode",
							res.data.qr_code
						);
					}

					if (!res.data.qr_code && method === "14") {
						router.push({ name: "PayLaterSuccess" });
					} else if (!res.data.qr_code) {
				router.push({ name: "PaymentSuccess" });
			}
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		})
				.finally(() => {
			return false;
		});
	} catch (e) {
		Sentry.captureException(e);
		return false;
	}
},

	/**
	 * Check for an order status update every 5 seconds.
	 */
	async keepCheckingForOrderStatus() {
		const selectedOrder = store.getters["order/getSelectedOrder"];
		let paymentStatus = null as any;

		await axios
			.get(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${selectedOrder.id}/mollie-status`,
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
		.then((res) => {
			// Order is updated.
			paymentStatus = res.data.status;
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
		});

	return paymentStatus;
	},

	/**
	 * Update the customer signature
	 */
	sendCustomerSignature: async function (signatureBase64: any) {
		await store.dispatch("order/setCustomerSignature", signatureBase64);
		await this.updateOrder();
	},

	/**
	 * Runs an action that needs the order refreshed afterwards, without losing
	 * unsaved order line edits.
	 *
	 * A refresh replaces the whole selected order with the server's copy, so a
	 * price or quantity the mechanic changed but has not saved yet would be
	 * thrown away — which is why a price set to €0 for warranty work
	 * disappeared the moment a photo was added, or the customer's e-mail
	 * address was corrected. Saving first is what keeps it: the server then
	 * echoes those values back in the refresh.
	 *
	 * Only a dirty order is saved, so the action does not trigger the side
	 * effects of an order update (order lines are recreated, the Mollie
	 * reference is cleared) when there is nothing to save.
	 *
	 * @param action the request whose result the refresh has to pick up
	 * @returns whether pending order line edits are now persisted
	 */
	withOrderRefresh: async function (
		action: () => Promise<unknown>
	): Promise<OrderRefreshResult> {
		const hasUnsavedOrderLines =
			store.getters["order/getOrderLinesHaveChanged"];

		// A failed save leaves those edits in memory only. The action still
		// runs — losing a photo is worse than a stale order — but the refresh
		// is skipped below, because it would overwrite them.
		const orderLinesArePersisted = hasUnsavedOrderLines
			? (await this.updateOrder()) === true
			: true;

		// The save replaced the order with the server's copy, so this is the
		// state the refresh is allowed to overwrite. Anything the mechanic
		// changes from here on is newer than what was persisted.
		const orderLinesAfterSave = this.serialiseOrderLines();

		try {
			await action();
		} catch (error) {
			// Only the caller knows what the action was, so it does the
			// reporting; a wrong message here would misdirect the mechanic.
			return { actionSucceeded: false, orderLinesArePersisted, error };
		}

		// An upload takes seconds on site, which is long enough for the
		// mechanic to change another price meanwhile. Refreshing would throw
		// that away, so compare the data itself: the dirty flag is also set by
		// the save above, which would make every save look like a fresh edit.
		const editedDuringAction =
			this.serialiseOrderLines() !== orderLinesAfterSave;

		if (!orderLinesArePersisted || editedDuringAction) {
			return { actionSucceeded: true, orderLinesArePersisted: false };
		}

		await this.getOrderById(store.getters["order/getSelectedOrder"].id);

		if (hasUnsavedOrderLines) {
			store.commit("order/setOrderLinesHaveChanged", {
				orderLinesHaveChanged: false,
			});
		}

		return { actionSucceeded: true, orderLinesArePersisted: true };
	},

	/**
	 * A comparable snapshot of the order lines, taken the same way
	 * OrderDetail's own watcher decides whether they changed.
	 */
	serialiseOrderLines: function (): string {
		return JSON.stringify(
			store.getters["order/getSelectedOrder"].order_lines
		);
	},

	/**
	 * Uploads one situation image without losing unsaved order line edits.
	 */
	uploadSituationImage: async function (
		imageFile: File
	): Promise<OrderRefreshResult> {
		return await this.withOrderRefresh(() =>
			this.updateSituationImages(imageFile)
		);
	},

	async updateAllImagesAndFetchOrder(images: any[]) {
		try {
			// Generate an array of promises for each image update
			const updatePromises = images.map((image) =>
				this.updateSituationImages(image.file || image.src)
			);

			// Wait for all image updates to complete
			await Promise.all(updatePromises);

			// Once all images are updated, fetch the order by ID
			const orderId = store.getters["order/getSelectedOrder"].id;

			// Optional: return or do something with the order
			await this.getOrderById(orderId);
		await ToastService.presentToast(
			"Alle foto's zijn opgeslagen",
			2500,
			"success"
		);
	} catch (error) {
		Sentry.captureException(error);
		await ToastService.presentToast(
			"Er is een fout opgetreden bij het opslaan van de foto's",
			2500,
			"danger"
		);
		throw error;
	}
	},

	/**
	 * Update situationImages on the selected order.
	 * @param imageFile
	 */
	updateSituationImages: async function (imageFile: File) {
		const formData = new FormData();
		formData.append("image", imageFile);

		return await axios
			.post(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${store.getters["order/getSelectedOrder"].id}/images`,
				formData,
				{
					headers: {
						...store.getters["auth/getAuthHeaders"],
						"Content-Type": "multipart/form-data",
					},
				}
			)
		.then((response) => {
			return response.data.data;
		})
		.catch((error) => {
			Sentry.captureException(error);
			if (error.response) {
				AuthService.checkForErrors(error);
			}
			throw error;
		});
	},

	/**
	 * The defective parts registered on an appointment, newest first.
	 * @param appointmentId
	 */
	getDefectiveParts: async function (appointmentId: number) {
		return await axios
			.get(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${appointmentId}/defective-parts`,
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
			.then((response) => response.data.data)
			.catch((error) => {
				Sentry.captureException(error);
				if (error.response) {
					AuthService.checkForErrors(error);
				}
				throw error;
			});
	},

	/**
	 * Makes the registered defective parts of an appointment equal to the given
	 * list: parts with an id are updated, new ones are created and registered
	 * parts that are missing are removed. Needs a connection: a rejected part has
	 * to be corrected by the mechanic, so it is not queued for later.
	 * @param appointmentId
	 * @param defectiveParts
	 */
	syncDefectiveParts: async function (
		appointmentId: number,
		defectiveParts: Array<{
			id: number | null;
			replaced_at: string;
			serial_number: string;
			article: string;
			comment: string | null;
		}>
	) {
		return await axios
			.put(
				process.env.VUE_APP_API_URL +
					`/mechanic/appointments/${appointmentId}/defective-parts`,
				{ defective_parts: defectiveParts },
				{
					headers: store.getters["auth/getAuthHeaders"],
				}
			)
			.then((response) => response.data.data)
			.catch((error) => {
				Sentry.captureException(error);
				if (error.response) {
					AuthService.checkForErrors(error);
				}
				throw error;
			});
	},
};
